/**
 * PMS validation utilities
 * Requirements: 2.1, 2.4, 2.5, 2.6
 */

import {
  CreateObjectiveInput,
  UpdateObjectiveInput,
  Objective,
  ObjectiveStatus,
} from './pms';

/**
 * Validation result interface
 */
export interface PMSValidationResult {
  isValid: boolean;
  errors: string[];
}

const OBJECTIVE_STATUSES = new Set(Object.values(ObjectiveStatus));

/**
 * Checks if a string value is empty or whitespace-only.
 */
function isBlank(value: unknown): boolean {
  return typeof value !== 'string' || value.trim().length === 0;
}

/**
 * Validates a CreateObjectiveInput.
 * Requirements: 2.1, 2.4
 */
export function validateCreateObjectiveInput(
  input: Partial<CreateObjectiveInput>
): PMSValidationResult {
  const errors: string[] = [];

  if (isBlank(input.reviewCycleId)) {
    errors.push('Review cycle ID is required');
  }

  if (isBlank(input.title)) {
    errors.push('Title is required');
  }

  if (isBlank(input.description)) {
    errors.push('Description is required');
  }

  if (isBlank(input.measureOfSuccess)) {
    errors.push('Measure of success is required');
  }

  if (isBlank(input.startDate)) {
    errors.push('Start date is required');
  }

  if (isBlank(input.endDate)) {
    errors.push('End date is required');
  }

  // Validate date range
  if (!isBlank(input.startDate) && !isBlank(input.endDate)) {
    if (input.startDate! >= input.endDate!) {
      errors.push('Start date must be before end date');
    }
  }

  // Validate weightage
  if (input.weightage === undefined || input.weightage === null) {
    errors.push('Weightage is required');
  } else if (typeof input.weightage !== 'number' || input.weightage < 0 || input.weightage > 100) {
    errors.push('Weightage must be a number between 0 and 100');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates an UpdateObjectiveInput.
 * Only validates fields that are present (partial update).
 * Requirements: 2.4, 2.5, 2.6
 */
export function validateUpdateObjectiveInput(
  input: Partial<UpdateObjectiveInput>
): PMSValidationResult {
  const errors: string[] = [];

  if (input.title !== undefined && isBlank(input.title)) {
    errors.push('Title cannot be empty');
  }

  if (input.description !== undefined && isBlank(input.description)) {
    errors.push('Description cannot be empty');
  }

  if (input.measureOfSuccess !== undefined && isBlank(input.measureOfSuccess)) {
    errors.push('Measure of success cannot be empty');
  }

  if (input.startDate !== undefined && isBlank(input.startDate)) {
    errors.push('Start date cannot be empty');
  }

  if (input.endDate !== undefined && isBlank(input.endDate)) {
    errors.push('End date cannot be empty');
  }

  // Validate date range if both provided
  if (input.startDate !== undefined && input.endDate !== undefined) {
    if (!isBlank(input.startDate) && !isBlank(input.endDate) && input.startDate! >= input.endDate!) {
      errors.push('Start date must be before end date');
    }
  }

  // Validate weightage
  if (input.weightage !== undefined) {
    if (typeof input.weightage !== 'number' || input.weightage < 0 || input.weightage > 100) {
      errors.push('Weightage must be a number between 0 and 100');
    }
  }

  // Validate progressPercentage
  if (input.progressPercentage !== undefined) {
    if (typeof input.progressPercentage !== 'number' || input.progressPercentage < 0 || input.progressPercentage > 100) {
      errors.push('Progress percentage must be a number between 0 and 100');
    }
  }

  // Validate status
  if (input.status !== undefined && !OBJECTIVE_STATUSES.has(input.status)) {
    errors.push(`Invalid status: "${input.status}"`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates a full Objective object for structural integrity.
 * Requirements: 2.1, 2.4
 */
export function validateObjective(
  objective: Partial<Objective>
): PMSValidationResult {
  const errors: string[] = [];

  if (isBlank(objective.id)) {
    errors.push('Objective ID is required');
  }

  if (isBlank(objective.employeeId)) {
    errors.push('Employee ID is required');
  }

  if (isBlank(objective.employeeName)) {
    errors.push('Employee name is required');
  }

  if (isBlank(objective.reviewCycleId)) {
    errors.push('Review cycle ID is required');
  }

  if (isBlank(objective.title)) {
    errors.push('Title is required');
  }

  if (isBlank(objective.description)) {
    errors.push('Description is required');
  }

  if (isBlank(objective.measureOfSuccess)) {
    errors.push('Measure of success is required');
  }

  if (isBlank(objective.startDate)) {
    errors.push('Start date is required');
  }

  if (isBlank(objective.endDate)) {
    errors.push('End date is required');
  }

  // Validate date range
  if (!isBlank(objective.startDate) && !isBlank(objective.endDate)) {
    if (objective.startDate! >= objective.endDate!) {
      errors.push('Start date must be before end date');
    }
  }

  // Validate weightage
  if (objective.weightage === undefined || objective.weightage === null) {
    errors.push('Weightage is required');
  } else if (typeof objective.weightage !== 'number' || objective.weightage < 0 || objective.weightage > 100) {
    errors.push('Weightage must be a number between 0 and 100');
  }

  // Validate progressPercentage
  if (objective.progressPercentage === undefined || objective.progressPercentage === null) {
    errors.push('Progress percentage is required');
  } else if (typeof objective.progressPercentage !== 'number' || objective.progressPercentage < 0 || objective.progressPercentage > 100) {
    errors.push('Progress percentage must be a number between 0 and 100');
  }

  if (!objective.status || !OBJECTIVE_STATUSES.has(objective.status)) {
    errors.push('Valid objective status is required');
  }

  if (isBlank(objective.managerId)) {
    errors.push('Manager ID is required');
  }

  if (isBlank(objective.managerName)) {
    errors.push('Manager name is required');
  }

  if (isBlank(objective.createdAt)) {
    errors.push('Created timestamp is required');
  }

  if (isBlank(objective.updatedAt)) {
    errors.push('Updated timestamp is required');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates that adding a new weightage won't exceed 100% total.
 * Requirements: 2.5, 2.6
 */
export function validateWeightageTotal(
  existingWeightages: number[],
  newWeightage: number
): PMSValidationResult {
  const currentTotal = existingWeightages.reduce((sum, w) => sum + w, 0);
  const newTotal = currentTotal + newWeightage;

  if (newTotal > 100) {
    return {
      isValid: false,
      errors: [
        `Total weightage would be ${newTotal}%, which exceeds 100%. Remaining available: ${100 - currentTotal}%`,
      ],
    };
  }

  return { isValid: true, errors: [] };
}
